import { isSafeContentHref } from "../../content.ts";
import type {
  ContentBlockNode,
  ContentInlineNode,
  ContentListItemNode,
  ContentMark,
  ContentParagraphNode,
} from "../../types.ts";
import { applyFieldAttributes, toDomId } from "../shared.ts";
import type { RenderContext, RenderField } from "../types.ts";

/**
 * Renders a display-only CONTENT field from its structured document.
 *
 * Every node is materialized with explicit DOM element and attribute calls;
 * text is always inserted as text nodes. No raw HTML string is ever assigned,
 * so the document cannot introduce markup beyond the permitted node types.
 */
export const renderContentField = (
  field: RenderField<"CONTENT">,
  context: RenderContext,
): HTMLElement => {
  const wrapper = document.createElement("div");
  applyFieldAttributes(wrapper, field);
  wrapper.classList.add("fb-content");
  wrapper.id = `${context.instanceId}-${toDomId(field.id)}`;

  for (const block of field.configJson.document.content) {
    wrapper.append(renderBlock(block));
  }

  return wrapper;
};

const renderBlock = (block: ContentBlockNode): HTMLElement => {
  switch (block.type) {
    case "heading": {
      const level = block.attrs.level;
      const heading = document.createElement(`h${level}`);
      appendInline(heading, block.content);

      return heading;
    }
    case "bulletList":
      return renderList("ul", block.content);
    case "orderedList": {
      const list = renderList("ol", block.content);
      if (block.attrs?.start !== undefined) {
        list.setAttribute("start", String(block.attrs.start));
      }

      return list;
    }
    default:
      return renderParagraph(block);
  }
};

const renderParagraph = (node: ContentParagraphNode): HTMLParagraphElement => {
  const paragraph = document.createElement("p");
  appendInline(paragraph, node.content);

  return paragraph;
};

const renderList = (tag: "ol" | "ul", items: ContentListItemNode[]): HTMLElement => {
  const list = document.createElement(tag);

  for (const item of items) {
    const listItem = document.createElement("li");
    for (const paragraph of item.content) {
      listItem.append(renderParagraph(paragraph));
    }
    list.append(listItem);
  }

  return list;
};

const appendInline = (parent: HTMLElement, nodes: ContentInlineNode[] | undefined): void => {
  if (!nodes) {
    return;
  }

  for (const node of nodes) {
    if (node.type === "hardBreak") {
      parent.append(document.createElement("br"));

      continue;
    }

    parent.append(renderTextNode(node.text, node.marks));
  }
};

const renderTextNode = (text: string, marks: ContentMark[] | undefined): Node => {
  const textNode = document.createTextNode(text);
  if (!marks || marks.length === 0) {
    return textNode;
  }

  // Wrap the text run from the innermost mark outward so nested emphasis and
  // links compose into valid, explicitly-built elements.
  let current: Node = textNode;
  for (const mark of marks) {
    current = applyMark(mark, current);
  }

  return current;
};

const applyMark = (mark: ContentMark, child: Node): HTMLElement => {
  if (mark.type === "bold") {
    const element = document.createElement("strong");
    element.append(child);

    return element;
  }

  if (mark.type === "italic") {
    const element = document.createElement("em");
    element.append(child);

    return element;
  }

  const anchor = document.createElement("a");
  anchor.append(child);

  // Defense in depth: only attach an href that re-passes the safety guard, and
  // isolate the destination document from the opener.
  if (isSafeContentHref(mark.attrs.href)) {
    anchor.setAttribute("href", mark.attrs.href);
    anchor.setAttribute("rel", "noopener noreferrer nofollow");
  }

  return anchor;
};
